import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Text, OutlineButton } from '@reservamos/elements';
import useWhitelabelTheme from 'hooks/whitelabel/useWhitelabelTheme';
import flexibleCalendar from 'images/icons/flexible-calendar.webp';
import flexibleCalendarFlat from 'images/icons/flexible-calendar-flat.webp';
import PreviousPrice from 'ui/atoms/PreviousPrice';
import NewPriceComponent from './NewPriceComponent';
import TripDiscountAdvice from './TripDiscountAdvice';

/**
 * TerminalGroupedOpenTicket component.
 * Renders the open ticket row shown inside a terminal group on the results page.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.selectTrip - The function to select a trip.
 * @param {number} props.total - The total price.
 * @param {Object} props.trip - The trip object.
 * @param {boolean} props.isExchange - Indicates if it is an exchange.
 * @param {number} props.walletPoints - The wallet points.
 * @param {boolean} props.showWalletPoints - Indicates if the wallet price should be shown.
 * @param {string} props.walletType - The wallet type.
 * @param {object} props.providerDiscount - Provider discount applied to the open ticket.
 * @param {object} props.pricing - Pricing details used by the shared results price component.
 * @param {boolean} props.showDiscountUrgency - Indicates if the urgency discount UI is enabled.
 * @param {boolean} props.showClassicDiscount - Indicates if the classic discount badge is shown.
 * @param {string} props.way - Search direction used to calculate the discount label.
 * @returns {JSX.Element} The rendered grouped open ticket row.
 */
const TerminalGroupedOpenTicket = ({
  selectTrip,
  total,
  trip,
  isExchange,
  walletPoints,
  showWalletPoints,
  walletType,
  providerDiscount,
  pricing,
  showDiscountUrgency,
  showClassicDiscount,
  way,
}) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation();
  const { resultsCTAColor } = useWhitelabelTheme();

  const isFlatFunnel = features.FUNNEL_STYLE === 'FLAT';
  const calendarIcon = isFlatFunnel ? flexibleCalendarFlat : flexibleCalendar;
  const totalBeforeDiscount = Number(pricing?.totalBeforeDiscount);
  const showPreviousPrice =
    Boolean(providerDiscount?.amount) &&
    (features.SHOW_OFFICE_PRICE || showDiscountUrgency) &&
    Number.isFinite(totalBeforeDiscount) &&
    totalBeforeDiscount > total;

  return (
    <div
      className="relative flex w-full flex-col items-stretch gap-3 rounded-2xl border border-gray-200 bg-white p-3.5 text-left md:min-h-[68px] md:flex-row md:items-center md:justify-between md:gap-4 md:rounded-3xl md:px-4 md:py-3 [&_.result-box-sale]:h-5 [&_.result-box-sale]:w-5"
      data-testid="open-ticket-button"
    >
      <div className="flex w-full min-w-0 items-center md:w-auto">
        <img
          src={calendarIcon}
          className="h-8 w-8 shrink-0 object-contain"
          data-testid="open-ticket-calendar-icon"
          data-funnel-style={isFlatFunnel ? 'flat' : 'classic'}
          alt=""
          aria-hidden="true"
        />
        <span className="ml-2.5 flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3.5">
          <span className="flex flex-wrap items-baseline gap-1 whitespace-normal md:flex-nowrap md:whitespace-nowrap">
            <Text size="S" mobileSize="S">
              {t('general:open_ticket_grouped_prefix')}
            </Text>
            <Text size="S" weight="semibold" mobileSize="S">
              {t('general:open_ticket_grouped_label')}
            </Text>
          </span>
          <Text size="S" color="grayMedium" mobileSize="XS">
            {t('general:open_ticket_grouped_description')}
          </Text>
        </span>
      </div>

      <div className="flex w-full items-center justify-between gap-3 border-t border-dashed border-gray-300 pt-3 md:ml-auto md:w-auto md:self-stretch md:border-l md:border-t-0 md:pl-6 md:pt-0">
        {showClassicDiscount && (
          <TripDiscountAdvice
            discountType={pricing?.discountType}
            discountAvailability={pricing?.discountAvailability}
            discountAmount={providerDiscount?.amount}
            discountPercent={providerDiscount?.percent}
            tripTotal={total}
            way={way}
          />
        )}
        <div className="flex items-center gap-2 whitespace-nowrap">
          {!isExchange && (
            <NewPriceComponent
              total={total}
              walletTotal={walletPoints}
              walletType={walletType}
              showWalletTotal={showWalletPoints}
              providerDiscount={providerDiscount}
              discountType={pricing?.discountType}
              totalBeforeDiscount={pricing?.totalBeforeDiscount}
              discountAvailability={pricing?.discountAvailability}
              discountExpiresAt={pricing?.meta?.availableUntil}
              showDiscountUrgency={showDiscountUrgency}
              priceSize="L"
              mobilePriceSize="L"
              secondaryPrice={
                showPreviousPrice ? (
                  <PreviousPrice price={String(totalBeforeDiscount)} lineThrough={false} />
                ) : null
              }
            />
          )}
        </div>
        <span className="inline-flex items-center justify-center whitespace-nowrap [&>button]:cursor-pointer">
          <OutlineButton
            isRounded
            variant={resultsCTAColor}
            text={t('general:select')}
            onClick={() => selectTrip(trip)}
          />
        </span>
      </div>
    </div>
  );
};

TerminalGroupedOpenTicket.propTypes = {
  selectTrip: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  trip: PropTypes.shape().isRequired,
  isExchange: PropTypes.bool.isRequired,
  walletPoints: PropTypes.number.isRequired,
  showWalletPoints: PropTypes.bool.isRequired,
  walletType: PropTypes.string,
  providerDiscount: PropTypes.shape({
    amount: PropTypes.number,
    percent: PropTypes.number,
  }),
  pricing: PropTypes.shape({
    discountType: PropTypes.string,
    totalBeforeDiscount: PropTypes.number,
    discountAvailability: PropTypes.number,
    meta: PropTypes.shape({
      availableUntil: PropTypes.string,
    }),
  }),
  showDiscountUrgency: PropTypes.bool,
  showClassicDiscount: PropTypes.bool,
  way: PropTypes.string,
};

TerminalGroupedOpenTicket.defaultProps = {
  walletType: undefined,
  providerDiscount: null,
  pricing: null,
  showDiscountUrgency: false,
  showClassicDiscount: false,
  way: 'departure',
};

export default TerminalGroupedOpenTicket;
